/**
 * DeadlineFilters Component
 *
 * Filter controls for deadlines: status, type, and date range.
 */

import { X } from 'lucide-react';
import {
  DeadlineStatus,
  DeadlineType,
  DEADLINE_STATUSES,
  DEADLINE_TYPES,
} from './types';

interface DeadlineFiltersProps {
  statusFilter: DeadlineStatus[];
  typeFilter: DeadlineType[];
  startDate: string;
  endDate: string;
  onStatusChange: (statuses: DeadlineStatus[]) => void;
  onTypeChange: (types: DeadlineType[]) => void;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onClearAll: () => void;
}

export function DeadlineFilters({
  statusFilter,
  typeFilter,
  startDate,
  endDate,
  onStatusChange,
  onTypeChange,
  onStartDateChange,
  onEndDateChange,
  onClearAll,
}: DeadlineFiltersProps): JSX.Element {
  const handleStatusToggle = (status: DeadlineStatus) => {
    if (statusFilter.includes(status)) {
      onStatusChange(statusFilter.filter((s) => s !== status));
    } else {
      onStatusChange([...statusFilter, status]);
    }
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as DeadlineType;
    if (value && !typeFilter.includes(value)) {
      onTypeChange([...typeFilter, value]);
    }
  };

  const removeTypeFilter = (type: DeadlineType) => {
    onTypeChange(typeFilter.filter((t) => t !== type));
  };

  const hasFilters = statusFilter.length > 0 || typeFilter.length > 0 || startDate || endDate;

  return (
    <div className="space-y-4">
      {/* Status Filter */}
      <div>
        <label className="label">Status</label>
        <div className="flex flex-wrap gap-2">
          {DEADLINE_STATUSES.map((status) => (
            <button
              key={status.value}
              onClick={() => handleStatusToggle(status.value)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                statusFilter.includes(status.value)
                  ? 'bg-evergreen text-white'
                  : 'bg-soft-neutral text-muted hover:bg-gray-200'
              }`}
            >
              {status.label}
            </button>
          ))}
        </div>
      </div>

      {/* Type Filter */}
      <div>
        <label className="label" htmlFor="type-filter">
          Type
        </label>
        <select
          id="type-filter"
          value=""
          onChange={handleTypeChange}
          className="input w-full"
        >
          <option value="">Add type filter...</option>
          {DEADLINE_TYPES.filter((t) => !typeFilter.includes(t.value)).map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
        {typeFilter.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {typeFilter.map((type) => {
              const label = DEADLINE_TYPES.find((t) => t.value === type)?.label || type;
              return (
                <span
                  key={type}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-evergreen/10 text-evergreen rounded-full text-sm"
                >
                  {label}
                  <button
                    onClick={() => removeTypeFilter(type)}
                    className="hover:bg-evergreen/20 rounded-full p-0.5"
                  >
                    <X size={12} />
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Date Range Filter */}
      <div>
        <label className="label">Due Date Range</label>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={startDate}
            onChange={(e) => onStartDateChange(e.target.value)}
            className="input flex-1"
            placeholder="Start date"
          />
          <span className="text-muted">to</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => onEndDateChange(e.target.value)}
            className="input flex-1"
            placeholder="End date"
          />
          {(startDate || endDate) && (
            <button
              onClick={() => {
                onStartDateChange('');
                onEndDateChange('');
              }}
              className="btn-ghost btn-sm p-1"
              aria-label="Clear dates"
            >
              <X size={16} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {/* Clear All */}
      {hasFilters && (
        <div className="pt-2 border-t border-soft-neutral">
          <button onClick={onClearAll} className="btn-ghost btn-sm text-muted hover:text-ink">
            Clear All Filters
          </button>
        </div>
      )}
    </div>
  );
}

export default DeadlineFilters;
